import { SearchClient } from 'typesense';
import myShopClient from './nearlive';

const searchCollectionsCache = new Map();

/**
 * Get Typesense client credentials, scoped to the current shop.
 */
const getSearchKey = async (organisationId, shopId = '') => {
	const client = await myShopClient();
	const { data } = await client.get(`/api/inventory/search-key/${organisationId}${shopId ? `/${shopId}` : ''}`);
	return data;
};

/**
 * Get Typesense client for the current shop.
 *
 * @param {string} organisationId
 * @param {string} [shopId]
 * @return {Promise<import('typesense/lib/Typesense/Documents').SearchableDocuments>}
 */
export const getSearchCollection = async (organisationId, shopId = '') => {
	if (searchCollectionsCache.has(organisationId)) return searchCollectionsCache.get(organisationId);
	if (searchCollectionsCache.has(shopId)) return searchCollectionsCache.get(shopId);

	const { host, apiKey, collectionName } = await getSearchKey(organisationId, shopId);

	const searchClient = new SearchClient({
		nodes: [{ host, port: 443, protocol: 'https' }],
		apiKey
	});
	const collection = searchClient.collections(collectionName).documents();

	searchCollectionsCache.set(shopId, collection);

	return collection;
};

/**
 * Parse filters from an object to a string for Typesense,
 * removing conflicting filters.
 *
 * @param {Record<string, boolean>} filters
 * @param {boolean} [isOrganisationStock=false]
 * @returns {string}
 */
export const parseFilters = (filters, isOrganisationStock = false) => {
	let parsedFilters = [];

	const enabledFilters = Object.entries(filters)
		.filter(([, value]) => value)
		.map(([key]) => key);

	const andFilters = enabledFilters.filter((filter) => !filter.startsWith('delivery_categories:='));
	for (const filter of andFilters) {
		if (filter === 'enabled_i:=true && has_errors:!=true' && enabledFilters.includes('enabled_i:=false')) continue;
		if (filter === 'enabled_i:=false' && enabledFilters.includes('enabled_i:=true && has_errors:!=true')) continue;
		if (filter === 'in_stock:=true' && enabledFilters.includes('in_stock:=false')) continue;
		if (filter === 'in_stock:=false' && enabledFilters.includes('in_stock:=true')) continue;
		parsedFilters.push(filter);
	}

	// Delivery categories are OR filters, so we need to group them together
	const deliveryCategoriesFilters = enabledFilters.filter((filter) => filter.startsWith('delivery_categories:='));
	if (deliveryCategoriesFilters.length) {
		const deliveryCategoriesFilter = deliveryCategoriesFilters.join(' || ');
		parsedFilters.push(`(${deliveryCategoriesFilter})`);
	}

	// In organisation stock, we have slightly different field names
	if (isOrganisationStock) {
		parsedFilters = parsedFilters.map((filter) => {
			filter = filter.replace('enabled_i', 'enabled');
			filter = filter.replace('productSource_i', 'productSource');
			return filter;
		});
	}

	return parsedFilters.join(' && ');
};

/**
 * Search inventory for a shop.
 * @param {object} params
 * @param {string} params.organisationId
 * @param {string} [params.shopId]
 * @param {number} params.page
 * @param {string} params.query
 * @param {Record<string, boolean>} params.filters
 * @returns {Promise<{ inventory: any[], numOfPages: number }>}
 */
export const listInventory = async ({ organisationId, shopId = '', page, query, filters }) => {
	const collection = await getSearchCollection(organisationId, shopId);

	const data = await collection.search(
		{
			q: query || '*',
			filter_by: parseFilters(filters, !shopId),
			query_by: 'title, barcode, category_titles, delivery_categories',
			facet_by: 'category_ids, delivery_categories',
			sort_by: 'barcode:asc',
			per_page: 50,
			page
		},
		{}
	);

	const inventory = data.hits.map(({ document }) => ({
		...document,
		enabled: document.enabled_i,
		productSource: document.productSource_i
	}));

	return {
		inventory,
		numOfPages: Math.ceil(data.found / 50)
	};
};

/**
 * Get existing delivery categories for a shop.
 * @param {object} params
 * @param {string} params.organisationId
 * @param {string} [params.shopId]
 * @returns {Promise<{ value: string, label: string }[]>}
 */
export const listDeliveryCategories = async ({ organisationId, shopId = '' }) => {
	const collection = await getSearchCollection(organisationId, shopId);

	const data = await collection.search(
		{
			q: shopId || organisationId,
			query_by: shopId ? 'shopId' : 'organisation_id',
			facet_by: 'delivery_categories',
			page: 1,
			per_page: 0,
			max_facet_values: 1000
		},
		{}
	);

	return data.facet_counts[0].counts.map(({ value }) => ({
		value,
		label: value
	}));
};
